import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MuiFileInput } from 'mui-file-input';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import SelectField from '../common/components/SelectField';
import deviceCategories from '../common/util/deviceCategories';
import { useTranslation } from '../common/components/LocalizationProvider';
import useDeviceAttributes from '../common/attributes/useDeviceAttributes';
import { useAdministrator, useDeviceReadonly } from '../common/util/permissions';
import SettingsMenu from './components/SettingsMenu';
import useCommonDeviceAttributes from '../common/attributes/useCommonDeviceAttributes';
import { useCatch } from '../reactHelper';
import useSettingsStyles from './common/useSettingsStyles';
import QrCodeDialog from '../common/components/QrCodeDialog';
import fetchOrThrow from '../common/util/fetchOrThrow';

const customAttributesDefinitions = {
  DESCRICAO: { name: 'DESCRICAO' },
  MODELO_VEICULO: { name: 'MODELO_VEICULO' },
  PLACA_VEICULO: { name: 'PLACA_VEICULO' },
  TIPO_DISPOSITIVO: { name: 'TIPO_DISPOSITIVO' },
  ANO_VEICULO: { name: 'ANO_VEICULO' },
  CHASSIS: { name: 'CHASSIS' },
  MARCA_VEICULO: { name: 'MARCA_VEICULO' },
  VALOR_FIPE: { name: 'VALOR_FIPE' },
  COR_VEICULO: { name: 'COR_VEICULO' },
  CAPACIDADE_TANQUE_LITROS: { name: 'CAPACIDADE_TANQUE_LITROS' },
  CUSTO_POR_LITRO: { name: 'CUSTO_POR_LITRO' },

  LOCAL_INSTALACAO: { name: 'LOCAL_INSTALACAO' },
  DATA_INSTALACAO: { name: 'DATA_INSTALACAO' },
  TECNICO_INSTALACAO: { name: 'TECNICO_INSTALACAO' },
  IMAGEM_LOCAL_INSTALACAO: { name: 'IMAGEM_LOCAL_INSTALACAO' },

  FABRICANTE_RASTREADOR: { name: 'FABRICANTE_RASTREADOR' },
  MODELO_RASTREADOR: { name: 'MODELO_RASTREADOR' },
  VALOR_EQUIPAMENTO: { name: 'VALOR_EQUIPAMENTO' },
  DETECCAO_IGNICAO: { name: 'DETECCAO_IGNICAO' },
  NOTAS_ADMINISTRATIVAS: { name: 'NOTAS_ADMINISTRATIVAS' },

  CHIP_FORNECEDOR: { name: 'CHIP_FORNECEDOR' },
  CHIP_OPERADORA: { name: 'CHIP_OPERADORA' },
  CHIP_ICCID: { name: 'CHIP_ICCID' },
  CHIP_NUMERO_SIM: { name: 'CHIP_NUMERO_SIM' },
  CHIP_APN: { name: 'CHIP_APN' },
  CHIP_VALOR_MENSAL: { name: 'CHIP_VALOR_MENSAL' },
  CHIP_DATA_ATIVACAO: { name: 'CHIP_DATA_ATIVACAO' },
  CHIP_DATA_VALIDADE: { name: 'CHIP_DATA_VALIDADE' },

  // Tag Config (K-Tag) - nomes que o integrador já espera
  kTagHashedKeys: { name: 'kTagHashedKeys' },
  kTagPrivKeys: { name: 'kTagPrivKeys' },
};

const ensureFixedAttributes = (attributes = {}) => {
  const result = { ...attributes };
  Object.keys(customAttributesDefinitions).forEach((key) => {
    if (!(key in result)) {
      result[key] = '';
    }
  });
  return result;
};

const isFixedAttributeKey = (key) =>
  !!customAttributesDefinitions[key] || key === 'deviceImage';

const filterExtraAttributes = (attributes = {}) => {
  const extra = {};
  Object.keys(attributes).forEach((key) => {
    if (!isFixedAttributeKey(key)) {
      extra[key] = attributes[key];
    }
  });
  return extra;
};

const DevicePage = () => {
  const { classes } = useSettingsStyles();
  const t = useTranslation();

  const admin = useAdministrator();
  const deviceReadonly = useDeviceReadonly();
  const nameOnlyReadonlyMode = deviceReadonly && !admin;

  const commonDeviceAttributes = useCommonDeviceAttributes(t);
  const deviceAttributes = useDeviceAttributes(t);

  const [searchParams] = useSearchParams();
  const uniqueId = searchParams.get('uniqueId');

  const [rawItem, internalSetItem] = useState(uniqueId ? { uniqueId } : null);
  const [showQr, setShowQr] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const readonlyUniqueIdRef = useRef(uniqueId || null);

  const setItem = (valueOrUpdater) => {
    if (typeof valueOrUpdater === 'function') {
      internalSetItem((prev) => {
        const next = valueOrUpdater(prev);
        if (!next) return next;
        return {
          ...next,
          attributes: ensureFixedAttributes(next.attributes),
        };
      });
    } else if (!valueOrUpdater) {
      internalSetItem(valueOrUpdater);
    } else {
      internalSetItem({
        ...valueOrUpdater,
        attributes: ensureFixedAttributes(valueOrUpdater.attributes),
      });
    }
  };

  const item = rawItem
    ? {
        ...rawItem,
        attributes: ensureFixedAttributes(rawItem.attributes),
      }
    : rawItem;

  useEffect(() => {
    if (deviceReadonly && item?.uniqueId && !readonlyUniqueIdRef.current) {
      readonlyUniqueIdRef.current = item.uniqueId;
    }
  }, [deviceReadonly, item?.uniqueId]);

  const getAttr = (key) =>
    item && item.attributes && item.attributes[key] != null
      ? item.attributes[key]
      : '';

  const handleAttrChange = (key) => (event) => {
    const value = event.target.value;
    setItem({
      ...item,
      attributes: {
        ...(item?.attributes || {}),
        [key]: value,
      },
    });
  };

  const handleFileInput = useCatch(async (newFile) => {
    setImageFile(newFile);

    if (newFile && item?.id) {
      const response = await fetchOrThrow(`/api/devices/${item.id}/image`, {
        method: 'POST',
        body: newFile,
      });

      const deviceImage = await response.text();

      setItem({
        ...item,
        attributes: {
          ...(item.attributes || {}),
          deviceImage,
        },
      });
    } else if (!newFile && item) {
      const remainingAttributes = Object.fromEntries(
        Object.entries(item.attributes || {}).filter(
          ([key]) => key !== 'deviceImage',
        ),
      );
      setItem({
        ...item,
        attributes: remainingAttributes,
      });
    }
  });

  const validate = () => item && item.name && item.uniqueId;
  const beforeSave = (currentItem) => {
    if (!deviceReadonly || !currentItem) {
      return currentItem;
    }

    // UI restrictions + client-side sanitization are complementary; backend validation remains mandatory.
    const lockedUniqueId =
      readonlyUniqueIdRef.current || currentItem.uniqueId || uniqueId || '';

    return {
      id: currentItem.id,
      name: currentItem.name,
      uniqueId: lockedUniqueId,
    };
  };

  return (
    <EditItemView
      endpoint="devices"
      item={item}
      setItem={setItem}
      validate={validate}
      beforeSave={beforeSave}
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'sharedDevice']}
    >
      {item && (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedRequired')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField
                value={item.name || ''}
                onChange={(event) =>
                  setItem({ ...item, name: event.target.value })
                }
                label={t('sharedName')}
              />
              <TextField
                value={item.uniqueId || ''}
                onChange={(event) =>
                  setItem({ ...item, uniqueId: event.target.value })
                }
                label={t('deviceIdentifier')}
                helperText={t('deviceIdentifierHelp')}
                disabled={Boolean(uniqueId) || nameOnlyReadonlyMode}
              />
            </AccordionDetails>
          </Accordion>

          {!nameOnlyReadonlyMode && (
            <>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    {t('sharedExtra')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <SelectField
                    value={item.groupId}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        groupId: Number(event.target.value),
                      })
                    }
                    endpoint="/api/groups"
                    label={t('groupParent')}
                  />
                  <TextField
                    value={item.phone || ''}
                    onChange={(event) =>
                      setItem({ ...item, phone: event.target.value })
                    }
                    label={t('sharedPhone')}
                  />
                  <TextField
                    value={item.model || ''}
                    onChange={(event) =>
                      setItem({ ...item, model: event.target.value })
                    }
                    label={t('deviceModel')}
                  />
                  <TextField
                    value={item.contact || ''}
                    onChange={(event) =>
                      setItem({ ...item, contact: event.target.value })
                    }
                    label={t('deviceContact')}
                  />
                  <SelectField
                    value={item.category || 'default'}
                    onChange={(event) =>
                      setItem({ ...item, category: event.target.value })
                    }
                    data={deviceCategories
                      .map((category) => ({
                        id: category,
                        name: t(
                          `category${category
                            .replace(/^\w/, (c) => c.toUpperCase())}`,
                        ),
                      }))
                      .sort((a, b) => a.name.localeCompare(b.name))}
                    label={t('deviceCategory')}
                  />
                  <SelectField
                    value={item.calendarId}
                    onChange={(event) =>
                      setItem({
                        ...item,
                        calendarId: Number(event.target.value),
                      })
                    }
                    endpoint="/api/calendars"
                    label={t('sharedCalendar')}
                  />
                  <TextField
                    label={t('userExpirationTime')}
                    type="date"
                    value={
                      item.expirationTime
                        ? item.expirationTime.split('T')[0]
                        : ''
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        setItem({
                          ...item,
                          expirationTime: new Date(
                            e.target.value,
                          ).toISOString(),
                        });
                      }
                    }}
                    disabled={!admin}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item.disabled || false}
                        onChange={(event) =>
                          setItem({
                            ...item,
                            disabled: event.target.checked,
                          })
                        }
                      />
                    }
                    label={t('sharedDisabled')}
                    disabled={!admin}
                  />
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowQr(true)}
                  >
                    {t('sharedQrCode')}
                  </Button>
                </AccordionDetails>
              </Accordion>

          {item.id && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  {t('attributeDeviceImage')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <MuiFileInput
                  placeholder={t('attributeDeviceImage')}
                  value={imageFile}
                  onChange={handleFileInput}
                  inputProps={{ accept: 'image/*' }}
                />
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Veículo</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField label="Descrição" value={getAttr('DESCRICAO')} onChange={handleAttrChange('DESCRICAO')} />
              <TextField label="Modelo do veículo" value={getAttr('MODELO_VEICULO')} onChange={handleAttrChange('MODELO_VEICULO')} />
              <TextField label="Placa do veículo" value={getAttr('PLACA_VEICULO')} onChange={handleAttrChange('PLACA_VEICULO')} />
              <TextField label="Tipo de dispositivo" value={getAttr('TIPO_DISPOSITIVO')} onChange={handleAttrChange('TIPO_DISPOSITIVO')} />
              <TextField label="Ano" value={getAttr('ANO_VEICULO')} onChange={handleAttrChange('ANO_VEICULO')} />
              <TextField label="Chassi" value={getAttr('CHASSIS')} onChange={handleAttrChange('CHASSIS')} />
              <TextField label="Marca" value={getAttr('MARCA_VEICULO')} onChange={handleAttrChange('MARCA_VEICULO')} />
              <TextField label="Valor Fipe" value={getAttr('VALOR_FIPE')} onChange={handleAttrChange('VALOR_FIPE')} />
              <TextField label="Cor do veículo" value={getAttr('COR_VEICULO')} onChange={handleAttrChange('COR_VEICULO')} />
              <TextField label="Capacidade do tanque (litros)" value={getAttr('CAPACIDADE_TANQUE_LITROS')} onChange={handleAttrChange('CAPACIDADE_TANQUE_LITROS')} />
              <TextField label="Custo por litro" value={getAttr('CUSTO_POR_LITRO')} onChange={handleAttrChange('CUSTO_POR_LITRO')} />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Instalação</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField
                label="Local da instalação"
                value={getAttr('LOCAL_INSTALACAO')}
                onChange={handleAttrChange('LOCAL_INSTALACAO')}
                multiline
              />
              <TextField
                label="Data da instalação"
                type="date"
                value={getAttr('DATA_INSTALACAO')}
                onChange={handleAttrChange('DATA_INSTALACAO')}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Técnico"
                value={getAttr('TECNICO_INSTALACAO')}
                onChange={handleAttrChange('TECNICO_INSTALACAO')}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Hardware</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField label="Fabricante do rastreador" value={getAttr('FABRICANTE_RASTREADOR')} onChange={handleAttrChange('FABRICANTE_RASTREADOR')} />
              <TextField label="Modelo do rastreador" value={getAttr('MODELO_RASTREADOR')} onChange={handleAttrChange('MODELO_RASTREADOR')} />
              <TextField label="Valor do equipamento" value={getAttr('VALOR_EQUIPAMENTO')} onChange={handleAttrChange('VALOR_EQUIPAMENTO')} />
              <TextField label="Detecção de ignição" value={getAttr('DETECCAO_IGNICAO')} onChange={handleAttrChange('DETECCAO_IGNICAO')} />
              <TextField label="Notas administrativas" value={getAttr('NOTAS_ADMINISTRATIVAS')} onChange={handleAttrChange('NOTAS_ADMINISTRATIVAS')} multiline />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Chip</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField label="Fornecedor" value={getAttr('CHIP_FORNECEDOR')} onChange={handleAttrChange('CHIP_FORNECEDOR')} />
              <TextField label="Operadora" value={getAttr('CHIP_OPERADORA')} onChange={handleAttrChange('CHIP_OPERADORA')} />
              <TextField label="ICCID" value={getAttr('CHIP_ICCID')} onChange={handleAttrChange('CHIP_ICCID')} />
              <TextField label="Número SIM" value={getAttr('CHIP_NUMERO_SIM')} onChange={handleAttrChange('CHIP_NUMERO_SIM')} />
              <TextField label="APN" value={getAttr('CHIP_APN')} onChange={handleAttrChange('CHIP_APN')} />
              <TextField label="Valor mensal" value={getAttr('CHIP_VALOR_MENSAL')} onChange={handleAttrChange('CHIP_VALOR_MENSAL')} />
              <TextField
                label="Data de ativação do SIM"
                type="date"
                value={getAttr('CHIP_DATA_ATIVACAO')}
                onChange={handleAttrChange('CHIP_DATA_ATIVACAO')}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Data de validade do SIM"
                type="date"
                value={getAttr('CHIP_DATA_VALIDADE')}
                onChange={handleAttrChange('CHIP_DATA_VALIDADE')}
                InputLabelProps={{ shrink: true }}
              />
            </AccordionDetails>
          </Accordion>

          {/* Tag Config */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Tag Config</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField
                label="kTagHashedKeys"
                value={getAttr('kTagHashedKeys')}
                onChange={handleAttrChange('kTagHashedKeys')}
              />
              <TextField
                label="kTagPrivKeys"
                value={getAttr('kTagPrivKeys')}
                onChange={handleAttrChange('kTagPrivKeys')}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Outros atributos</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <EditAttributesAccordion
                attributes={filterExtraAttributes(item.attributes || {})}
                setAttributes={(extraAttributes) =>
                  setItem((prev) => {
                    if (!prev) return prev;

                    const prevAttributes = prev.attributes || {};
                    const fixedAttributes = Object.fromEntries(
                      Object.entries(prevAttributes).filter(([key]) =>
                        isFixedAttributeKey(key),
                      ),
                    );

                    return {
                      ...prev,
                      attributes: {
                        ...ensureFixedAttributes(fixedAttributes),
                        ...extraAttributes,
                      },
                    };
                  })
                }
                definitions={{
                  ...commonDeviceAttributes,
                  ...deviceAttributes,
                }}
              />
            </AccordionDetails>
          </Accordion>
            </>
          )}
        </>
      )}
      <QrCodeDialog open={showQr} onClose={() => setShowQr(false)} />
    </EditItemView>
  );
};

export default DevicePage;
